/**
 * Data Service Factory
 *
 * Returns the appropriate data service based on environment configuration.
 * - When useDirectDynamoDB is true: Use dynamoDBService (direct DynamoDB access)
 * - When useDirectDynamoDB is false: Use nexusApiOrchestrationService (REST API)
 *
 * This allows us to use stable DynamoDB access in Production/Personal environments
 * while testing REST APIs in Beta/Gamma environments.
 */

import { getEnvironmentConfig } from '../configurations/config';
import { dynamoDBService } from './dynamodbService';
import { nexusApiOrchestrationService } from './nexus-api';
import {
  MappingWithReviews,
  CommonControlsHierarchyResponse,
  MappingReview,
  MappingFeedback,
  FrameworkControl,
  Framework,
  ReviewQueryParams,
  DynamoDBQueryResponse,
  MappingQueryParams,
} from '../types/dynamodb';

/**
 * Unified interface for data service operations
 * Both dynamoDBService and nexusApiOrchestrationService implement these methods
 */
export interface DataService {
  // Mappings operations
  getAllMappingsWithReviews(params?: MappingQueryParams): Promise<MappingWithReviews[]>;
  getMappingWithReviews(mappingKey: string): Promise<MappingWithReviews | null>;
  getAllMappingsForLabeling(currentUserId: string): Promise<MappingWithReviews[]>;
  getAvailableFrameworks(): Promise<Framework[]>;

  // Common Controls hierarchy (NEX-1062)
  getCommonControlsWithHierarchy(): Promise<CommonControlsHierarchyResponse>;

  // Reviews operations
  getReviewsForMapping(mappingKey: string): Promise<MappingReview[]>;
  getReviewsByReviewer(params: ReviewQueryParams): Promise<DynamoDBQueryResponse<MappingReview>>;
  submitReview(review: Omit<MappingReview, 'reviewKey' | 'submittedAt'>): Promise<void>;

  // Suggestions & labeling
  suggestMapping(params: {
    controlKey: string;
    mappedControlKey: string;
    reviewerId: string;
    reviewerName: string;
    justification: string;
  }): Promise<void>;
  submitLabel(params: {
    mappingKey: string;
    controlKey: string;
    mappedControlKey: string;
    reviewerId: string;
    reviewerName: string;
    decision: 'label-yes' | 'label-no';
    feedback?: string;
  }): Promise<void>;

  // Feedback submissions
  submitFinalFeedback(feedback: Omit<MappingFeedback, 'timestamp'>): Promise<void>;

  // Feedback operations (NEX-1064)
  getUserFeedbackForMappings(
    mappingIds: string[],
    userId: string
  ): Promise<Map<string, { label: string; feedback?: string; feedbackId: string }>>;

  submitFeedback(
    mappingId: string,
    data: {
      feedbackProviderId: string;
      label: 'thumbs_up' | 'thumbs_down';
      decision: 'agree' | 'disagree';
      feedback?: string;
    }
  ): Promise<{ message: string; mappingKey: string; feedbackId: string; label: string }>;

  updateFeedback(
    mappingId: string,
    feedbackId: string,
    data: {
      label?: 'thumbs_up' | 'thumbs_down';
      decision?: 'agree' | 'disagree';
      feedback?: string;
    }
  ): Promise<{ message: string; mappingKey: string; feedbackId: string }>;

  // Framework controls
  batchGetFrameworkControls(
    controls: Array<{ frameworkKey: string; controlKey: string }>
  ): Promise<Map<string, FrameworkControl>>;
}

/**
 * Required method names that all DataService implementations must provide
 */
const REQUIRED_DATA_SERVICE_METHODS = [
  'getAllMappingsWithReviews',
  'getMappingWithReviews',
  'getAllMappingsForLabeling',
  'getAvailableFrameworks',
  'getCommonControlsWithHierarchy',
  'getReviewsForMapping',
  'getReviewsByReviewer',
  'submitReview',
  'suggestMapping',
  'submitLabel',
  'submitFinalFeedback',
  'getUserFeedbackForMappings',
  'submitFeedback',
  'updateFeedback',
  'batchGetFrameworkControls',
] as const;

/**
 * Validates that a service implements all required DataService methods at runtime.
 * This prevents "undefined is not a function" errors when a service is missing methods.
 *
 * @param service - The service object to validate
 * @param serviceName - Name of the service for error messages
 * @returns The validated service cast as DataService
 * @throws Error if any required method is missing
 */
function validateDataService(service: unknown, serviceName: string): DataService {
  // Guard against null/undefined before accessing properties
  if (!service || typeof service !== 'object') {
    throw new Error(
      `${serviceName} is null, undefined, or not an object. ` +
      `Expected a valid service instance implementing DataService interface.`
    );
  }

  for (const method of REQUIRED_DATA_SERVICE_METHODS) {
    if (typeof (service as Record<string, unknown>)[method] !== 'function') {
      throw new Error(
        `${serviceName} is missing required DataService method: ${method}. ` +
        `Ensure the service implements all methods defined in the DataService interface.`
      );
    }
  }
  return service as DataService;
}

/**
 * Get the appropriate data service based on environment configuration
 *
 * @returns DataService - Either dynamoDBService or nexusApiOrchestrationService
 */
export function getDataService(): DataService {
  const config = getEnvironmentConfig();

  if (config.useDirectDynamoDB) {
    console.log(`📊 Using DynamoDB service for ${config.stageName} environment`);
    return validateDataService(dynamoDBService, 'dynamoDBService');
  }

  console.log(`🌐 Using REST API service for ${config.stageName} environment`);
  return validateDataService(
    nexusApiOrchestrationService,
    'nexusApiOrchestrationService'
  );
}

/**
 * Check if the current environment uses direct DynamoDB access
 */
export function isUsingDirectDynamoDB(): boolean {
  const config = getEnvironmentConfig();
  return config.useDirectDynamoDB;
}

// Export individual services for cases where specific service is needed
export { dynamoDBService } from './dynamodbService';
export { nexusApiOrchestrationService } from './nexus-api';
